import React, { useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import './AddMovieModal.css';

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY || '3a59c0eeb226d403b56307471865f112';
const TMDB_API_URL = 'https://api.themoviedb.org/3/search/movie';
const TMDB_IMAGE_URL = 'https://image.tmdb.org/t/p/w500';

const AddMovieModal = ({ onClose, onAddMovie }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [watchedYear, setWatchedYear] = useState(new Date().getFullYear());
  const [status, setStatus] = useState('want-to-watch');

  const fetchMovies = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(TMDB_API_URL, {
        params: {
          api_key: TMDB_API_KEY,
          query: query,
        },
      });
      setSearchResults(response.data.results.slice(0, 7));
    } catch (error) {
      console.error('Error fetching movies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchMovies = useCallback(debounce(fetchMovies, 300), []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedMovie(null); // Reset selection if user types again
    debouncedFetchMovies(query);
  };

  const handleSelectMovie = (movie) => {
    setSelectedMovie(movie);
    setSearchQuery(movie.title);
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMovie) {
      alert('Please select a movie from the search results.');
      return;
    }
    
    const movieData = {
      title: selectedMovie.title,
      year: selectedMovie.release_date ? parseInt(selectedMovie.release_date.split('-')[0]) : null,
      poster: selectedMovie.poster_path ? `${TMDB_IMAGE_URL}${selectedMovie.poster_path}` : '',
      status: status,
      watchedOn: status === 'watched' ? watchedYear : null,
    };
    
    await onAddMovie(movieData);
    onClose(); // Explicitly close the modal after adding
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Add New Movie</h2>
          <button onClick={onClose} className="close-button">✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="add-movie-form">
          <div className="form-group autocomplete-group">
            <label htmlFor="title">Movie Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Start typing a movie title..."
              autoComplete="off"
            />
            {isLoading && <div className="spinner"></div>}
            {searchResults.length > 0 && (
              <ul className="autocomplete-results">
                {searchResults.map((movie) => (
                  <li key={movie.id} onClick={() => handleSelectMovie(movie)}>
                    <img 
                      src={movie.poster_path ? `${TMDB_IMAGE_URL}${movie.poster_path}` : 'https://via.placeholder.com/40x60'} 
                      alt={movie.title}
                      className="autocomplete-poster"
                    />
                    <span>{movie.title} ({movie.release_date ? movie.release_date.split('-')[0] : 'N/A'})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {selectedMovie && (
            <>
              <div className="selected-movie-preview">
                <img src={selectedMovie.poster_path ? `${TMDB_IMAGE_URL}${selectedMovie.poster_path}` : ''} alt={selectedMovie.title} />
                <h3>{selectedMovie.title}</h3>
              </div>
              
              <div className="form-group">
                <label htmlFor="status">Add to</label>
                <select
                  id="status"
                  name="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="want-to-watch">Want to Watch</option>
                  <option value="watched">Watched</option>
                </select>
              </div>

              {status === 'watched' && (
                <div className="form-group">
                  <label htmlFor="watchedYear">Year Watched</label>
                  <input
                    type="number"
                    id="watchedYear"
                    name="watchedYear"
                    value={watchedYear}
                    onChange={(e) => setWatchedYear(parseInt(e.target.value))}
                    placeholder="e.g. 2024"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
              )}
            </>
          )}
          
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="add-btn" disabled={!selectedMovie}>
              Add Movie
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMovieModal; 